import { jsPDF } from "jspdf";
import { sprintService } from "../../api/taskBoardService";
import { uploadFile, deleteFile, IMAGES_BUCKET, isSupabaseStorageAvailable } from "../../api/supabaseStorage";
import { bucketPath } from "../../api/taskBoardService";
import { STATUS_LABELS, PRIORITIES } from "./constants";

/**
 * Sprint documents (Sprint Plan, Retrospective report). The content is always
 * the API's data (computed from the tasks); this file only lays it out as a PDF
 * and stores it in the sprint's attachments through the API. The API refuses
 * the save when the data changed meanwhile (STALE_DOCUMENT), so a saved PDF
 * always matches the data it was built from.
 */

// Dates are stored as UTC midnight of the chosen day.
export const fmtDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-GB", { timeZone: "UTC", day: "numeric", month: "short", year: "numeric" })
    : "-";

const priorityLabel = (p) => PRIORITIES.find((x) => x.value === p)?.label || "-";

// The built-in PDF fonts cover Latin text only: map common typographic
// characters to ASCII instead of printing garbage.
const safe = (text) =>
  String(text ?? "")
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, "-")
    .replace(/…/g, "...")
    .replace(/→/g, "->")
    .replace(/ /g, " ");

const MARGIN = 16;
const COLORS = {
  ink: [30, 30, 35],
  muted: [110, 110, 120],
  accent: [200, 90, 10],
  line: [220, 220, 227],
  panel: [247, 247, 250],
  head: [238, 238, 243],
  white: [255, 255, 255],
};
const PRIORITY_COLORS = { critical: [190, 40, 40], high: [190, 40, 40], medium: [180, 110, 0], low: [110, 110, 120] };

/** Small flowing-layout writer on top of jsPDF (A4, automatic page breaks). */
const createWriter = ({ footerText }) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const inner = width - MARGIN * 2;
  const bottom = height - 16;
  let y = MARGIN;

  const font = (size, style = "normal", color = COLORS.ink) => {
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
    doc.setTextColor(...color);
  };
  const lh = (size) => size * 0.44;
  const lines = (text, size, maxWidth, style = "normal") => {
    font(size, style);
    return doc.splitTextToSize(safe(text), maxWidth);
  };
  const ensure = (needed) => {
    if (y + needed > bottom) {
      doc.addPage();
      y = MARGIN;
      return true;
    }
    return false;
  };

  // Coloured title band at the top of the first page.
  const header = ({ title, subtitle, meta }) => {
    doc.setFillColor(...COLORS.accent);
    doc.rect(0, 0, width, 38, "F");
    font(22, "bold", COLORS.white);
    const titleLines = doc.splitTextToSize(safe(title), inner);
    doc.text(titleLines[0], MARGIN, 16);
    font(11, "normal", [255, 232, 214]);
    doc.text(safe(subtitle), MARGIN, 23.5);
    font(10, "normal", COLORS.white);
    doc.text(safe(meta), MARGIN, 31);
    y = 48;
  };

  // A row of summary cards: [{ label, value }].
  const stats = (items) => {
    const gap = 3;
    const w = (inner - gap * (items.length - 1)) / items.length;
    ensure(20);
    items.forEach((item, i) => {
      const x = MARGIN + i * (w + gap);
      doc.setFillColor(...COLORS.panel);
      doc.setDrawColor(...COLORS.line);
      doc.roundedRect(x, y, w, 17, 2, 2, "FD");
      font(15, "bold");
      doc.text(safe(String(item.value)), x + 4, y + 8.5);
      font(8, "normal", COLORS.muted);
      doc.text(safe(item.label), x + 4, y + 13.5);
    });
    y += 17 + 4;
  };

  const section = (title) => {
    ensure(16);
    y += 3;
    doc.setFillColor(...COLORS.accent);
    doc.rect(MARGIN, y, 1.6, 5.2, "F");
    font(12.5, "bold");
    doc.text(safe(title), MARGIN + 4, y + 4.1);
    y += 8;
    doc.setDrawColor(...COLORS.line);
    doc.line(MARGIN, y, width - MARGIN, y);
    y += 3.5;
  };

  const paragraph = (text, { size = 10, color = COLORS.ink } = {}) => {
    lines(text, size, inner).forEach((l) => {
      ensure(lh(size));
      font(size, "normal", color);
      doc.text(l, MARGIN, y + lh(size) * 0.8);
      y += lh(size);
    });
    y += 2;
  };

  const bullets = (items) => {
    items.forEach((text) => {
      const ls = lines(text, 9.5, inner - 5);
      ensure(lh(9.5) * ls.length);
      font(9.5, "normal", COLORS.accent);
      doc.text("-", MARGIN + 1, y + lh(9.5) * 0.8);
      font(9.5);
      ls.forEach((l) => {
        doc.text(l, MARGIN + 5, y + lh(9.5) * 0.8);
        y += lh(9.5);
      });
      y += 1.4;
    });
    y += 1;
  };

  const none = (text = "None.") => paragraph(text, { color: COLORS.muted });

  // Greedy word wrap of coloured runs [{ text, color, bold }] → lines of runs.
  const wrapRuns = (runs, maxWidth, size) => {
    const out = [[]];
    let used = 0;
    runs.forEach((run) => {
      font(size, run.bold ? "bold" : "normal");
      safe(run.text)
        .split(/(\s+)/)
        .filter(Boolean)
        .forEach((word) => {
          const wWidth = doc.getTextWidth(word);
          const isSpace = /^\s+$/.test(word);
          if (used + wWidth > maxWidth && !isSpace && used > 0) {
            out.push([]);
            used = 0;
          }
          if (isSpace && used === 0) return; // no leading space on a line
          out[out.length - 1].push({ text: word, color: run.color, bold: run.bold, width: wWidth });
          used += wWidth;
        });
    });
    return out;
  };

  /**
   * Table with a repeating header and zebra rows. `columns`: [{ label, width
   * (mm, 0 = remaining space), align: "left" | "center" }]; a row is an array
   * of cells: a string, { text, sub, color, bold } (`sub` = muted small text
   * underneath) or { runs: [{ text, color, bold }] } for mixed-colour text.
   */
  const table = (columns, rows) => {
    const fixed = columns.reduce((n, c) => n + c.width, 0);
    const widths = columns.map((c) => c.width || inner - fixed);
    const pad = 2;
    const textX = (i, x, textWidth) => (columns[i].align === "center" ? x + (widths[i] - textWidth) / 2 : x + pad);
    const headRow = () => {
      ensure(9);
      doc.setFillColor(...COLORS.head);
      doc.rect(MARGIN, y, inner, 7, "F");
      font(8.5, "bold", COLORS.muted);
      let x = MARGIN;
      columns.forEach((c, i) => {
        doc.text(safe(c.label), textX(i, x, doc.getTextWidth(safe(c.label))), y + 4.8);
        x += widths[i];
      });
      y += 7;
    };
    headRow();
    rows.forEach((row, r) => {
      const cells = row.map((cell, i) => {
        const c = typeof cell === "string" ? { text: cell } : cell;
        if (c.runs) return { c, runLines: wrapRuns(c.runs, widths[i] - pad * 2, 9.5), main: [], sub: [] };
        const main = lines(c.text, 9.5, widths[i] - pad * 2, c.bold ? "bold" : "normal");
        const sub = c.sub ? lines(c.sub, 8, widths[i] - pad * 2) : [];
        return { c, main, sub, runLines: null };
      });
      const h =
        Math.max(
          ...cells.map((x) =>
            x.runLines
              ? x.runLines.length * lh(9.5)
              : x.main.length * lh(9.5) + x.sub.length * lh(8) + (x.sub.length ? 1.2 : 0),
          ),
        ) + 4;
      if (ensure(Math.min(h, bottom - MARGIN))) headRow();
      if (r % 2 === 0) {
        doc.setFillColor(...COLORS.panel);
        doc.rect(MARGIN, y, inner, h, "F");
      }
      let x = MARGIN;
      cells.forEach(({ c, main, sub, runLines }, i) => {
        let ty = y + 2 + lh(9.5) * 0.8;
        if (runLines) {
          runLines.forEach((line) => {
            const total = line.reduce((n, run) => n + run.width, 0);
            let rx = textX(i, x, total);
            line.forEach((run) => {
              font(9.5, run.bold ? "bold" : "normal", run.color || COLORS.ink);
              doc.text(run.text, rx, ty);
              rx += run.width;
            });
            ty += lh(9.5);
          });
        } else {
          font(9.5, c.bold ? "bold" : "normal", c.color || COLORS.ink);
          main.forEach((l) => {
            doc.text(l, textX(i, x, doc.getTextWidth(l)), ty);
            ty += lh(9.5);
          });
          if (sub.length) {
            ty += 1.2;
            font(8, "normal", COLORS.muted);
            sub.forEach((l) => {
              doc.text(l, x + pad, ty);
              ty += lh(8);
            });
          }
        }
        x += widths[i];
      });
      y += h;
    });
    doc.setDrawColor(...COLORS.line);
    doc.line(MARGIN, y, width - MARGIN, y);
    y += 4;
  };

  const finish = () => {
    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i += 1) {
      doc.setPage(i);
      font(8, "normal", COLORS.muted);
      doc.setDrawColor(...COLORS.line);
      doc.line(MARGIN, height - 12, width - MARGIN, height - 12);
      doc.text(safe(footerText), MARGIN, height - 7.5);
      doc.text(`Page ${i} of ${pages}`, width - MARGIN, height - 7.5, { align: "right" });
    }
    return doc.output("blob");
  };
  return { header, stats, section, paragraph, bullets, none, table, finish };
};

const DAY_MS = 24 * 60 * 60 * 1000;
const utcDayMs = (v) => Math.floor(new Date(v).getTime() / DAY_MS) * DAY_MS;
const shortDay = (ms) =>
  new Date(ms).toLocaleDateString("en-GB", { timeZone: "UTC", day: "numeric", month: "short" });
const DONE_COLOR = [30, 130, 60];

/**
 * Timeline PDF: one row per day of the sprint (days outside it are added when
 * a task is due then) with that day's tasks joined by "+", finished ones in
 * green. Built from the API's timeline rows (the sprint's task set).
 */
export const buildSprintTimelinePdf = ({ sprint, tasks }) => {
  const w = createWriter({ footerText: `${sprint.name} - Timeline - generated ${fmtDate(new Date())}` });
  w.header({ title: sprint.name, subtitle: "Sprint Timeline", meta: timeframe(sprint) });

  const byDay = new Map();
  const undated = [];
  tasks.forEach((t) => {
    if (!t.dueDate) return undated.push(t);
    const key = utcDayMs(t.dueDate);
    byDay.set(key, [...(byDay.get(key) || []), t]);
  });
  const days = [utcDayMs(sprint.startDate), utcDayMs(sprint.endDate), ...byDay.keys()];
  const first = Math.min(...days);
  const last = Math.min(Math.max(...days), first + 120 * DAY_MS);

  const done = tasks.filter((t) => t.status === "done").length;
  w.stats([
    { label: "Tasks", value: tasks.length },
    { label: "Completed", value: done },
    { label: "Remaining", value: tasks.length - done },
  ]);

  const runsFor = (items) =>
    items.flatMap((t, i) => [
      ...(i ? [{ text: " + " }] : []),
      { text: t.title, color: t.status === "done" ? DONE_COLOR : undefined },
    ]);
  const rows = [];
  for (let d = first; d <= last; d += DAY_MS) {
    const items = (byDay.get(d) || []).sort((a, b) => (a.number || 0) - (b.number || 0));
    rows.push([{ text: shortDay(d), bold: true }, items.length ? { runs: runsFor(items) } : "-"]);
  }
  if (undated.length) rows.push([{ text: "No date", bold: true }, { runs: runsFor(undated) }]);

  w.section("Deliverables by date");
  w.table(
    [
      { label: "Date", width: 28, align: "center" },
      { label: "Deliverables", width: 0, align: "center" },
    ],
    rows,
  );
  w.paragraph("Green = completed.", { size: 8.5, color: COLORS.muted });
  return w.finish();
};

const timeframe = (sprint) => `${fmtDate(sprint.startDate)} - ${fmtDate(sprint.endDate)}`;

/**
 * Sprint Plan PDF from GET /sprints/:id/plan. No assignee, squad, complexity
 * or task status: statuses change constantly and would keep the PDF outdated.
 */
export const buildSprintPlanPdf = (plan) => {
  const { sprint, summary } = plan;
  const w = createWriter({ footerText: `${sprint.name} - Sprint Plan - generated ${fmtDate(plan.generatedAt)}` });
  w.header({ title: sprint.name, subtitle: "Sprint Plan", meta: timeframe(sprint) });

  w.stats([
    { label: "Total tasks", value: summary.totalTasks },
    { label: "From previous sprint", value: summary.carryOverFromCount },
    ...(plan.carryOverToNextFinal ? [{ label: "To next sprint", value: summary.carryOverToNextCount }] : []),
  ]);

  w.section("Sprint goal");
  w.paragraph(sprint.goal || "No goal set.", { color: sprint.goal ? COLORS.ink : COLORS.muted });
  if (sprint.description) {
    w.section("Description");
    w.paragraph(sprint.description);
  }

  w.section(`Tasks (${summary.totalTasks})`);
  if (!plan.tasks.length) w.none("No tasks in this sprint.");
  else {
    w.table(
      [
        { label: "#", width: 10 },
        { label: "Task", width: 0 },
        { label: "Priority", width: 24 },
        { label: "Deadline", width: 28 },
      ],
      plan.tasks.map((t) => [
        String(t.index),
        { text: `#${t.number}  ${t.title}`, bold: true, sub: t.descriptionText ? t.descriptionText.replace(/\n{2,}/g, "\n") : undefined },
        { text: priorityLabel(t.priority), color: PRIORITY_COLORS[t.priority] || COLORS.muted, bold: true },
        fmtDate(t.dueDate),
      ]),
    );
  }

  w.section("Blockers");
  if (plan.retrospectiveBlockers) w.paragraph(plan.retrospectiveBlockers);
  else w.none("None recorded.");

  w.section("Carry-over from previous sprint");
  if (!plan.carryOverFromPrevious.length) w.none();
  else w.bullets(plan.carryOverFromPrevious.map((t) => `#${t.number} ${t.title}  (from ${t.fromSprint.name || "previous sprint"})`));

  // Only once the sprint is over; before that these are just its open tasks.
  if (plan.carryOverToNextFinal) {
    w.section("Carry-over to next sprint");
    if (!plan.carryOverToNext.length) w.none();
    else w.bullets(plan.carryOverToNext.map((t) => `#${t.number} ${t.title}${t.movedTo ? `  (moved to ${t.movedTo.name})` : ""}`));
  }
  return w.finish();
};

/** Retrospective PDF from GET /sprints/:id/retrospective (saved retrospective only). */
export const buildRetrospectivePdf = ({ sprint, retrospective: r }) => {
  const w = createWriter({ footerText: `${sprint.name} - Retrospective - completed ${fmtDate(r.completedAt)}` });
  w.header({
    title: sprint.name,
    subtitle: "Sprint Retrospective",
    meta: `${timeframe(sprint)}   |   Completed ${fmtDate(r.completedAt)}`,
  });
  w.stats([
    { label: "Total tasks", value: r.stats.totalTasks },
    { label: "Completed", value: r.stats.completedTasks },
    { label: "Incomplete", value: r.stats.incompleteTasks },
    { label: "Completion rate", value: `${r.stats.completionRate}%` },
  ]);
  if (sprint.goal) {
    w.section("Sprint goal");
    w.paragraph(sprint.goal);
  }
  [
    ["What went well", r.wentWell],
    ["What went wrong", r.wentWrong],
    ["Blockers", r.blockers],
    ["Improvement notes", r.improvements],
  ].forEach(([title, text]) => {
    w.section(title);
    if (text) w.paragraph(text);
    else w.none("Nothing noted.");
  });
  w.section("Incomplete tasks (carry-over to next sprint)");
  if (!r.incompleteList?.length) w.none("None - every task was completed.");
  else w.bullets(r.incompleteList.map((t) => `#${t.number} ${t.title}  (${STATUS_LABELS[t.status] || t.status})`));
  return w.finish();
};

const slug = (s) => s.replace(/[^\w-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");

/**
 * Upload a generated PDF and register it on the sprint. On success the
 * replaced document's file is removed from storage; if registering fails
 * (e.g. the data changed meanwhile → STALE_DOCUMENT) the uploaded file is
 * removed again and the error is thrown.
 */
export const saveSprintDocument = async ({ sprint, kind, blob, sourceHash, label }) => {
  const name = `${slug(sprint.name)}_${label}.pdf`;
  const path = `sprints/${sprint._id}/${kind}-${Date.now()}-${name}`;
  const file = new File([blob], name, { type: "application/pdf" });
  const { publicUrl, error } = await uploadFile(IMAGES_BUCKET, path, file);
  if (error || !publicUrl) throw new Error(error?.message || "Upload failed");
  try {
    const res = await sprintService.saveDocument(sprint._id, kind, {
      name,
      url: publicUrl,
      size: file.size,
      contentType: "application/pdf",
      sourceHash,
    });
    (res.data.replaced || []).forEach((old) => {
      const oldPath = bucketPath(old.url);
      if (oldPath) deleteFile(IMAGES_BUCKET, oldPath).catch(() => {});
    });
    return res.data.sprint;
  } catch (err) {
    deleteFile(IMAGES_BUCKET, path).catch(() => {});
    throw err;
  }
};

/**
 * Build a sprint document from the API's current data and save it (replacing
 * the previous one of its kind). If the tasks change while it is being made the
 * API refuses the save (STALE_DOCUMENT) and it is rebuilt once from fresh data.
 * Returns the updated sprint (attachments).
 */
const syncSprintDocument = async (sprint, { kind, label, fetchData, build }) => {
  if (!isSupabaseStorageAvailable()) throw new Error("File storage is not configured");
  for (let attempt = 0; ; attempt += 1) {
    const data = await fetchData();
    try {
      return await saveSprintDocument({ sprint, kind, blob: build(data), sourceHash: data.hash, label });
    } catch (err) {
      if (err?.response?.data?.code !== "STALE_DOCUMENT" || attempt >= 1) throw err;
    }
  }
};

export const syncSprintPlanDocument = (sprint) =>
  syncSprintDocument(sprint, {
    kind: "plan",
    label: "Sprint-Plan",
    fetchData: async () => (await sprintService.plan(sprint._id)).data,
    build: buildSprintPlanPdf,
  });

export const syncSprintTimelineDocument = (sprint) =>
  syncSprintDocument(sprint, {
    kind: "timeline",
    label: "Timeline",
    fetchData: async () => (await sprintService.timeline(sprint._id)).data,
    build: (data) => buildSprintTimelinePdf({ sprint, tasks: data.tasks }),
  });

export const downloadBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
